import { useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import LeadDialog from "@/components/LeadDialog";

const positioning = [
  {
    label: "Category",
    headline: "Network Intelligence",
    sub: "Security, operations and acceleration in one platform",
  },
  {
    label: "Differentiation",
    headline: "Unified Platform",
    sub: "Built together rather than assembled from disconnected tools",
  },
  {
    label: "Portfolio",
    headline: "A growing product family",
    sub: "Enterprise infrastructure and Halo consumer products",
  },
  {
    label: "Opportunity",
    headline: "Enterprise + Consumer",
    sub: "A common platform across business networks and Halo",
  },
];

const visionItems = [
  "High-performance networking",
  "Advanced cyber resilience",
  "Distributed operational intelligence",
  "Simplified infrastructure control",
  "Future-ready security architecture",
];

export default function Investors() {
  const [investorOpen, setInvestorOpen] = useState(false);

  return (
    <>
      <SEO
        title="Company and Investors — LightningFlow"
        description="Learn about LightningFlow’s platform, product family and approach to secure intelligent infrastructure."
        canonicalPath="/investors"
      />

      {/* HERO */}
      <section className="lf-hero-editorial" data-testid="investors-hero">
        <div className="lf-container grid gap-12 py-20 md:py-28 lg:grid-cols-[1.15fr_.85fr] lg:items-center lg:py-32">
          <div>
          <div className="lf-hero-kicker">Company · Investors</div>
          <h1 className="lf-hero-title max-w-6xl">
            Intelligent network <em>infrastructure.</em>
          </h1>
          <p className="lf-hero-summary">
            LightningFlow addresses a growing enterprise need: networks that are secure, intelligent, high-performing and easier to operate. Its integrated platform creates expansion opportunities across enterprise infrastructure, edge security, AI-assisted operations and Halo consumer connectivity.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setInvestorOpen(true)}
              className="lf-btn-primary"
              data-testid="investors-cta-briefing"
            >
              Book a briefing call
              <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <a
              href="mailto:contact@lightningflow.ai?subject=LightningFlow%20Investor%20Overview"
              className="lf-btn-secondary"
              data-testid="investors-cta-readiness-packet"
            >
              Email for overview pack
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
          </div>
          </div>
          <aside className="border border-white/15 bg-black/25 p-6 text-white md:p-8" aria-label="Company snapshot">
            <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">Snapshot</div>
            <dl className="mt-6 space-y-5">
              <div><dt className="text-sm text-slate-400">Category</dt><dd className="mt-1 font-display text-2xl font-bold">Network intelligence</dd></div>
              <div><dt className="text-sm text-slate-400">Focus</dt><dd className="mt-1 font-display text-2xl font-bold">Secure, intelligent networks</dd></div>
              <div><dt className="text-sm text-slate-400">Portfolio</dt><dd className="mt-1 font-display text-2xl font-bold">Enterprise + consumer</dd></div>
              <div><dt className="text-sm text-slate-400">Home</dt><dd className="mt-1 font-display text-2xl font-bold">Australia</dd></div>
            </dl>
          </aside>
        </div>
      </section>

      {/* Positioning grid */}
      <section className="lf-section bg-ink-50/40 border-y border-ink-100" data-testid="investors-positioning">
        <div className="lf-container">
          <div className="lf-eyebrow">Investment thesis</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] max-w-3xl">
            A platform built for a growing need.
          </h2>
          <div className="lf-editorial-list">
            {positioning.map((p, i) => (
              <article key={p.label} data-testid={`investors-positioning-${i + 1}`}><span className="lf-list-index">0{i + 1}</span><div><p className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">{p.label}</p><h3 className="mt-3">{p.headline}</h3></div><p>{p.sub}</p></article>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="lf-section" data-testid="investors-vision">
        <div className="lf-container grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="lf-eyebrow">Long-term vision</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Building the Next Generation of Digital Infrastructure.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-ink-600 text-base md:text-lg leading-relaxed">
            <p>
              LightningFlow is built to improve how organizations secure, optimize, and operate modern infrastructure.
            </p>
            <p className="text-ink-500">The platform delivers:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {visionItems.map((v) => (
                <li key={v} className="flex items-start gap-3 border-l border-ink-100 pl-4 py-1">
                  <span className="font-display text-base font-semibold text-ink-900">{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="lf-section bg-ink-900 text-white" data-testid="investors-cta-band">
        <div className="lf-container grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="lf-eyebrow text-ink-300">Investor next step</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
              Investor briefing on request.
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => setInvestorOpen(true)}
              className="lf-btn bg-white text-ink-900 hover:bg-electric hover:text-white border border-white hover:border-electric"
              data-testid="investors-cta-briefing-bottom"
            >
              Book a briefing call
              <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <a
              href="mailto:contact@lightningflow.ai"
              className="lf-btn bg-transparent text-white border border-white/30 hover:border-white"
              data-testid="investors-cta-email"
            >
              contact@lightningflow.ai
            </a>
          </div>
        </div>
      </section>

      <LeadDialog
        open={investorOpen}
        onOpenChange={setInvestorOpen}
        kind="investor"
        title="Book a briefing call"
        description="Share your stage focus and diligence priorities."
      />
    </>
  );
}
